import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader, CheckCircle2, User, Building2, MapPin, Receipt, Tag, X } from 'lucide-react';
import { getCountries, getStates } from 'country-state-picker';
import SidePanelAdmin from '@CommonFunctionalDisplay/SidePanelAdmin';
import TopPanelAdmin from '@CommonFunctionalDisplay/TopPanelAdmin';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import BillingService from '@Helpers/URLHelpers/BillingService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import { detectBrowserPricingCurrency, formatBillingPrice, resolveBillingCurrency, fetchExchangeRate } from '../../billingPricing';
import './CheckoutPage.scss';

const COUNTRIES = getCountries();

const billingService = new BillingService();

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const parseServiceBody = (responseBody) => {
    if (!responseBody) return null;

    const parsedBody = JSON.parse(responseBody);
    return parsedBody?.data ?? parsedBody;
};

const resolveCountryName = (profile) => {
    const fullName = profile?.address?.country || profile?.country || '';
    if (fullName) return fullName;
    const code = (profile?.countryCode || profile?.address?.countryCode || '').toLowerCase();
    if (!code) return '';
    return COUNTRIES.find((c) => c.code.toLowerCase() === code)?.name || '';
};

const getBillingProfileFormValues = (profile, defaults) => ({
    ...defaults,
    billingName: profile?.billingName || profile?.legalName || defaults.billingName,
    workEmail: profile?.billingEmail || profile?.email || defaults.workEmail,
    phone: profile?.phone || '',
    line1: profile?.address?.line1 || profile?.addressLine1 || '',
    line2: profile?.address?.line2 || profile?.addressLine2 || '',
    city: profile?.address?.city || profile?.city || '',
    state: profile?.address?.state || profile?.state || '',
    pincode: profile?.address?.pincode || profile?.postalCode || '',
    country: resolveCountryName(profile) || defaults.country || '',
    gstNumber: profile?.gstNumber || '',
    panNumber: profile?.panNumber || '',
});

export default function CheckoutPage() {

    const navigate = useNavigate();
    const location = useLocation();
    const { currentOrg } = useReduxOrg();
    const { activeUser } = useReduxActiveUser();
    const { clearCurrentSubscription } = useReduxSubscription();

    const planState = location.state;
    const plan = planState?.plan;
    const billingCycle = planState?.billingCycle || 'MONTHLY';
    const browserPricingCurrency = detectBrowserPricingCurrency();

    const razorpayKeyRef    = useRef(null);
    const scriptPromiseRef  = useRef(null);

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [, setExchangeRateReady] = useState(false);
    const [couponInput, setCouponInput] = useState(planState?.couponCode || '');
    const [couponCode, setCouponCode] = useState(planState?.couponCode || '');
    const [couponResult, setCouponResult] = useState(planState?.couponResult || null);
    const [couponValidating, setCouponValidating] = useState(false);
    const [form, setForm] = useState({
        fullName: '',
        workEmail: '',
        phone: '',
        billingName: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
        gstNumber: '',
        panNumber: '',
    });

    const className = 'checkoutPage';

    useEffect(() => {
        if (!plan) {
            navigate('/subscribe', { replace: true });
            return;
        }
        // Kick off script + config + profile loading in parallel — errors are non-fatal here.
        loadRazorpayScript();
        loadProfile();
        fetchExchangeRate().then(() => setExchangeRateReady(true));
        billingService.fetchBillingConfig()
            .then(data => { if (data?.razorpayKeyId) razorpayKeyRef.current = data.razorpayKeyId; })
            .catch(() => {});
    }, []);

    /**
     * Returns a Promise that resolves once window.Razorpay is available.
     * Safe to call multiple times — reuses the same promise.
     */
    const loadRazorpayScript = () => {
        if (scriptPromiseRef.current) return scriptPromiseRef.current;
        scriptPromiseRef.current = new Promise((resolve) => {
            if (window.Razorpay) { resolve(); return; }
            const existing = document.getElementById('razorpay-checkout-js');
            if (existing) { existing.addEventListener('load', resolve); return; }
            const script = document.createElement('script');
            script.id = 'razorpay-checkout-js';
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = resolve;
            script.onerror = resolve; // resolve anyway; gateway check below will catch the failure
            document.body.appendChild(script);
        });
        return scriptPromiseRef.current;
    };

    const loadProfile = async () => {
        const defaults = {
            fullName: activeUser?.fullName || '',
            workEmail: activeUser?.email || '',
            billingName: currentOrg?.name || '',
            country: browserPricingCurrency === 'INR' ? 'India' : '',
        };

        if (!currentOrg?.id) {
            setForm((prev) => ({ ...prev, ...defaults }));
            setLoadingProfile(false);
            return;
        }

        try {
            const data = await billingService.getBillingProfile(currentOrg.id);
            if (data?.body) {
                const profile = parseServiceBody(data.body);
                if (profile && !profile.error) {
                    setForm((prev) => ({
                        ...prev,
                        ...getBillingProfileFormValues(profile, defaults),
                    }));
                    setLoadingProfile(false);
                    return;
                }
            }
        } catch (_) {}

        setForm((prev) => ({ ...prev, ...defaults }));
        setLoadingProfile(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'country') {
            setForm((prev) => ({ ...prev, country: value, state: '' }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const selectedCountry = COUNTRIES.find((c) => c.name === form.country);
    const availableStates = selectedCountry ? getStates(selectedCountry.code) : [];
    const billingCurrency = resolveBillingCurrency(form.country);

    const handleUpperCase = (name) => (e) => {
        setForm((prev) => ({ ...prev, [name]: e.target.value.toUpperCase() }));
    };

    const handleApplyCoupon = async () => {
        const code = couponInput.trim().toUpperCase();
        if (!code) return;
        setCouponValidating(true);
        try {
            const data = await billingService.validateCoupon(code, plan?.id);
            if (data?.body) {
                const parsed = JSON.parse(data.body);
                const result = parsed?.data ?? parsed;
                setCouponResult(result);
                if (result.valid) {
                    setCouponCode(code);
                    SnackbarManager.show(`Coupon applied: ${result.discountPercent}% off!`, 'success');
                } else {
                    setCouponCode('');
                    SnackbarManager.show(result.invalidReason || 'Invalid or expired coupon.', 'error');
                }
            }
        } catch (_) {
            SnackbarManager.show('Could not validate coupon. Please try again.', 'error');
        }
        setCouponValidating(false);
    };

    const handleRemoveCoupon = () => {
        setCouponInput('');
        setCouponCode('');
        setCouponResult(null);
    };

    const getBasePrice = () => {
        if (!plan) return null;
        const paise = billingCycle === 'MONTHLY' ? plan.priceMonthlyPaise : plan.priceAnnualPaise;
        if (!paise || paise <= 0) return 0;
        return paise;
    };

    const priceSuffix = billingCycle === 'MONTHLY' ? '/mo' : '/yr';

    const basePaise = getBasePrice();
    const discountPaise = couponResult?.valid && basePaise > 0
        ? Math.round(basePaise * couponResult.discountPercent / 100)
        : 0;
    const finalPaise = basePaise > 0 ? basePaise - discountPaise : 0;

    const validate = () => {
        const requiredFields = [
            ['fullName', 'Full name'],
            ['workEmail', 'Work email'],
            ['phone', 'Phone number'],
            ['billingName', 'Legal / billing name'],
            ['line1', 'Address line 1'],
            ['country', 'Country'],
            ['state', 'State / Province'],
            ['city', 'City'],
            ['pincode', 'Pincode'],
        ];
        for (const [key, label] of requiredFields) {
            if (!form[key].trim()) {
                SnackbarManager.show(`${label} is required.`, 'error');
                return false;
            }
        }
        if (form.gstNumber.trim() && !GST_REGEX.test(form.gstNumber.trim())) {
            SnackbarManager.show('Invalid GST number format. Expected: 22AAAAA0000A1Z5', 'error');
            return false;
        }
        if (form.panNumber.trim() && !PAN_REGEX.test(form.panNumber.trim())) {
            SnackbarManager.show('Invalid PAN number format. Expected: AAAAA0000A', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            // Step 1: Save billing profile
            const profilePayload = {
                orgId: currentOrg.id,
                billingName: form.billingName.trim(),
                email: form.workEmail.trim(),
                phone: form.phone.trim(),
                address: {
                    line1: form.line1.trim(),
                    line2: form.line2.trim(),
                    city: form.city.trim(),
                    state: form.state.trim(),
                    pincode: form.pincode.trim(),
                    country: form.country,
                },
                ...(form.gstNumber.trim() && { gstNumber: form.gstNumber.trim() }),
                ...(form.panNumber.trim() && { panNumber: form.panNumber.trim() }),
            };
            await billingService.upsertBillingProfile(profilePayload);

            // Step 2: Create the Razorpay subscription on the backend
            const subscriptionPayload = {
                orgId: currentOrg.id,
                planId: plan.id,
                billingCycle,
                agentSeatCount: 1,
                customerEmail: form.workEmail.trim(),
                customerName: form.fullName.trim(),
                customerPhone: form.phone.trim(),
                ...(couponCode?.trim() && { couponCode: couponCode.trim() }),
            };
            const data = await billingService.createSubscription(subscriptionPayload);
            if (!data?.body) {
                SnackbarManager.show('Could not start payment. Please try again.', 'error');
                setSubmitting(false);
                return;
            }

            const subscription = parseServiceBody(data.body);

            if (!subscription || subscription.success === false) {
                SnackbarManager.show(subscription?.message || 'Could not start payment. Please try again.', 'error');
                setSubmitting(false);
                return;
            }

            const razorpaySubId = subscription?.razorpaySubId;

            if (!razorpaySubId) {
                // Free plan — subscription is immediately active; clear so Routes re-fetches the real state
                clearCurrentSubscription();
                navigate('/checkout/success', { replace: true });
                return;
            }

            // Ensure Razorpay script is fully loaded
            await loadRazorpayScript();

            // Ensure key is available (fetch if not yet resolved)
            if (!razorpayKeyRef.current) {
                try {
                    const config = await billingService.fetchBillingConfig();
                    if (config?.razorpayKeyId) razorpayKeyRef.current = config.razorpayKeyId;
                } catch (_) {}
            }

            if (!razorpayKeyRef.current || !window.Razorpay) {
                SnackbarManager.show('Payment gateway not ready. Please refresh and try again.', 'error');
                setSubmitting(false);
                return;
            }

            // Step 3: Open Razorpay JS checkout modal (no redirect needed)
            setSubmitting(false);
            const rzp = new window.Razorpay({
                key: razorpayKeyRef.current,
                subscription_id: razorpaySubId,
                name: 'KaizIQ',
                description: plan.name,
                theme: { color: '#6366f1' },
                prefill: {
                    name: form.fullName.trim(),
                    email: form.workEmail.trim(),
                    contact: form.phone.trim(),
                },
                handler: async (response) => {
                    const paymentId = response.razorpay_payment_id;
                    const subscriptionId = response.razorpay_subscription_id;

                    setSubmitting(true);
                    try {
                        await billingService.confirmPayment(currentOrg.id, paymentId, subscriptionId);
                    } catch (_) {
                        // Backend confirm failed — navigate anyway; webhook will reconcile
                    }

                    clearCurrentSubscription();
                    navigate('/checkout/success', {
                        replace: true,
                        state: { paymentId, subscriptionId },
                    });
                },
            });

            rzp.on('payment.failed', (response) => {
                SnackbarManager.show(
                    response.error?.description || 'Payment failed. Please try again.',
                    'error'
                );
            });

            rzp.open();

        } catch (_) {
            SnackbarManager.show('Something went wrong. Please try again.', 'error');
            setSubmitting(false);
        }
    };

    if (loadingProfile) {
        return (
            <div className={className + '__loading'}>
                <Loader size={32} className={className + '__loading__spinner'} />
                <p>Loading checkout...</p>
            </div>
        );
    }

    return (
        <div className={className}>
            <SidePanelAdmin />
            <TopPanelAdmin />
            <div className={className + '__body'}>

                <button type="button" className={className + '__back'} onClick={() => navigate('/subscribe')}>
                    <ArrowLeft size={15} /> Back to plans
                </button>

                <div className={className + '__layout'}>

                    {/* ── Order summary ── */}
                    <aside className={className + '__summary'}>
                        <div className={className + '__summary__card'}>
                            <span className={className + '__summary__eyebrow'}>Order Summary</span>
                            <h3 className={className + '__summary__plan-name'}>{plan?.name}</h3>
                            <p className={className + '__summary__cycle'}>
                                {billingCycle === 'MONTHLY' ? 'Billed monthly' : 'Billed annually'}
                            </p>

                            <div className={className + '__summary__divider'} />

                            {/* ── Coupon ── */}
                            <div className={className + '__summary__coupon'}>
                                {couponResult?.valid ? (
                                    <>
                                        <div className={className + '__summary__coupon__applied'}>
                                            <Tag size={13} />
                                            <span className={className + '__summary__coupon__code'}>{couponCode}</span>
                                            <span className={className + '__summary__coupon__discount'}>{couponResult.discountPercent}% off</span>
                                            <button
                                                type="button"
                                                className={className + '__summary__coupon__remove'}
                                                onClick={handleRemoveCoupon}
                                                aria-label="Remove coupon"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                        <p className={className + '__summary__coupon__scope'}>
                                            {couponResult.scope === 'LIFETIME'
                                                ? 'Discount applies to every renewal'
                                                : 'Discount applies to first payment only'}
                                        </p>
                                    </>
                                ) : (
                                    <div className={className + '__summary__coupon__row'}>
                                        <input
                                            type="text"
                                            className={className + '__summary__coupon__input' + (couponResult && !couponResult.valid ? ' ' + className + '__summary__coupon__input--error' : '')}
                                            placeholder="Coupon code"
                                            value={couponInput}
                                            onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponResult(null); }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                            disabled={couponValidating}
                                        />
                                        <button
                                            type="button"
                                            className={className + '__summary__coupon__apply'}
                                            onClick={handleApplyCoupon}
                                            disabled={couponValidating || !couponInput.trim()}
                                        >
                                            {couponValidating ? <Loader size={12} /> : 'Apply'}
                                        </button>
                                    </div>
                                )}
                                {couponResult && !couponResult.valid && (
                                    <p className={className + '__summary__coupon__error'}>
                                        {couponResult.invalidReason || 'Invalid or expired coupon.'}
                                    </p>
                                )}
                            </div>

                            <div className={className + '__summary__rows'}>
                                <div className={className + '__summary__row'}>
                                    <span>Subtotal</span>
                                    <span className={discountPaise > 0 ? className + '__summary__strikethrough' : ''}>
                                        {formatBillingPrice(basePaise, billingCurrency)}{basePaise > 0 ? priceSuffix : ''}
                                    </span>
                                </div>
                                {discountPaise > 0 && (
                                    <div className={className + '__summary__row ' + className + '__summary__row--discount'}>
                                        <span>Discount ({couponResult.discountPercent}% off)</span>
                                        <span>− {formatBillingPrice(discountPaise, billingCurrency)}</span>
                                    </div>
                                )}
                                <div className={className + '__summary__row ' + className + '__summary__row--total'}>
                                    <span>{billingCurrency === 'INR' && finalPaise > 0 ? 'Total (excl. GST)' : 'Total'}</span>
                                    <span>{formatBillingPrice(finalPaise, billingCurrency)}{finalPaise > 0 ? priceSuffix : ''}</span>
                                </div>
                            </div>

                            {billingCurrency === 'INR' && finalPaise > 0 && (
                                <p className={className + '__summary__gst-note'}>
                                    18% GST will be added at checkout.
                                </p>
                            )}

                            <div className={className + '__summary__divider'} />

                            <div className={className + '__summary__limits'}>
                                <span>
                                    {plan?.maxAgents === -1 ? 'Unlimited agents' : `Up to ${plan?.maxAgents} agents`}
                                </span>
                                <span>
                                    {plan?.ticketLimitMonthly === -1
                                        ? 'Unlimited tickets/mo'
                                        : `${plan?.ticketLimitMonthly} tickets/mo`}
                                </span>
                            </div>

                            <div className={className + '__summary__secure'}>
                                <CheckCircle2 size={13} />
                                Payments secured by Razorpay
                            </div>
                        </div>
                    </aside>

                    {/* ── Billing form ── */}
                    <form className={className + '__form'} onSubmit={handleSubmit} noValidate>

                        {/* Contact */}
                        <section className={className + '__section'}>
                            <div className={className + '__section__header'}>
                                <User size={15} />
                                <h4>Contact Information</h4>
                            </div>
                            <div className={className + '__grid'}>
                                <label className={className + '__field'}>
                                    <span>Full name <em>*</em></span>
                                    <input
                                        name="fullName"
                                        type="text"
                                        value={form.fullName}
                                        onChange={handleChange}
                                        placeholder="Jane Smith"
                                        autoComplete="name"
                                    />
                                </label>
                                <label className={className + '__field'}>
                                    <span>Work email <em>*</em></span>
                                    <input
                                        name="workEmail"
                                        type="email"
                                        value={form.workEmail}
                                        onChange={handleChange}
                                        placeholder="jane@company.com"
                                        autoComplete="email"
                                    />
                                </label>
                                <label className={className + '__field'}>
                                    <span>Phone number <em>*</em></span>
                                    <input
                                        name="phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+91 98765 43210"
                                        autoComplete="tel"
                                    />
                                </label>
                            </div>
                        </section>

                        {/* Company */}
                        <section className={className + '__section'}>
                            <div className={className + '__section__header'}>
                                <Building2 size={15} />
                                <h4>Company / Billing Name</h4>
                            </div>
                            <div className={className + '__grid'}>
                                <label className={className + '__field ' + className + '__field--full'}>
                                    <span>Legal / billing name <em>*</em></span>
                                    <input
                                        name="billingName"
                                        type="text"
                                        value={form.billingName}
                                        onChange={handleChange}
                                        placeholder="Acme Private Limited"
                                        autoComplete="organization"
                                    />
                                </label>
                            </div>
                        </section>

                        {/* Address */}
                        <section className={className + '__section'}>
                            <div className={className + '__section__header'}>
                                <MapPin size={15} />
                                <h4>Billing Address</h4>
                            </div>
                            <div className={className + '__grid'}>
                                <label className={className + '__field ' + className + '__field--full'}>
                                    <span>Address line 1 <em>*</em></span>
                                    <input
                                        name="line1"
                                        type="text"
                                        value={form.line1}
                                        onChange={handleChange}
                                        placeholder="123, MG Road"
                                        autoComplete="address-line1"
                                    />
                                </label>
                                <label className={className + '__field ' + className + '__field--full'}>
                                    <span>Address line 2</span>
                                    <input
                                        name="line2"
                                        type="text"
                                        value={form.line2}
                                        onChange={handleChange}
                                        placeholder="Floor 4, Suite 400 (optional)"
                                        autoComplete="address-line2"
                                    />
                                </label>
                                <label className={className + '__field'}>
                                    <span>Country <em>*</em></span>
                                    <select
                                        name="country"
                                        value={form.country}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select country</option>
                                        {COUNTRIES.map((c) => (
                                            <option key={c.code} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className={className + '__field'}>
                                    <span>State / Province <em>*</em></span>
                                    {!form.country ? (
                                        <select disabled>
                                            <option value="">Select a country first</option>
                                        </select>
                                    ) : availableStates.length > 0 ? (
                                        <select
                                            name="state"
                                            value={form.state}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select state</option>
                                            {availableStates.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            name="state"
                                            type="text"
                                            value={form.state}
                                            onChange={handleChange}
                                            placeholder="State / Province / Region"
                                            autoComplete="address-level1"
                                        />
                                    )}
                                </label>
                                <label className={className + '__field'}>
                                    <span>City <em>*</em></span>
                                    <input
                                        name="city"
                                        type="text"
                                        value={form.city}
                                        onChange={handleChange}
                                        placeholder="Mumbai"
                                        autoComplete="address-level2"
                                    />
                                </label>
                                <label className={className + '__field'}>
                                    <span>Pincode <em>*</em></span>
                                    <input
                                        name="pincode"
                                        type="text"
                                        value={form.pincode}
                                        onChange={handleChange}
                                        placeholder="400001"
                                        maxLength={6}
                                        autoComplete="postal-code"
                                    />
                                </label>
                            </div>
                        </section>

                        {/* Tax */}
                        <section className={className + '__section'}>
                            <div className={className + '__section__header'}>
                                <Receipt size={15} />
                                <h4>
                                    Tax Details
                                    <span className={className + '__section__optional'}> — optional</span>
                                </h4>
                            </div>
                            <p className={className + '__section__hint'}>
                                Provide your GST number to receive a tax-compliant invoice. Appears on all invoices generated for your account.
                            </p>
                            <div className={className + '__grid'}>
                                <label className={className + '__field'}>
                                    <span>GST number</span>
                                    <input
                                        name="gstNumber"
                                        type="text"
                                        value={form.gstNumber}
                                        onChange={handleUpperCase('gstNumber')}
                                        placeholder="22AAAAA0000A1Z5"
                                        maxLength={15}
                                    />
                                </label>
                                <label className={className + '__field'}>
                                    <span>PAN number</span>
                                    <input
                                        name="panNumber"
                                        type="text"
                                        value={form.panNumber}
                                        onChange={handleUpperCase('panNumber')}
                                        placeholder="AAAAA0000A"
                                        maxLength={10}
                                    />
                                </label>
                            </div>
                        </section>

                        <div className={className + '__actions'}>
                            <button
                                type="submit"
                                className={className + '__submit'}
                                disabled={submitting}
                            >
                                {submitting
                                    ? <Loader size={18} className={className + '__submit__spinner'} />
                                    : <>Proceed to Payment <ArrowRight size={18} /></>
                                }
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}
